import React, { useEffect } from 'react'
import TemplateInput from '@/components/ui/TemplateInput'
import type { WorkflowNodeData } from '@/types/workflow'
import { useSettingsStore } from '@/stores/settingsStore'
import { WarningCircle, LockSimple } from '@phosphor-icons/react'
import { usePlanFeatures } from '@/hooks/usePlanFeatures'

interface InternalAlertConfigProps {
  data: WorkflowNodeData
  updateField: (field: string, value: any) => void
}

import {
  inputClass,
  inputStyle,
  sectionClass,
  dangerBannerClass,
  infoBannerClass,
  checkboxInputClass,
} from './_kit'
import { Select } from '@/components/ui/Select'
import { Field } from '@/components/ui/Field'

const CHANNEL_OPTIONS = [
  { value: 'email', label: 'Email' },
  { value: 'slack', label: 'Slack' },
  { value: 'teams', label: 'Microsoft Teams' },
  { value: 'in_app', label: 'In-App' },
]

/** The two channels the `team_alerts` plan feature gates. Email and in-app
 *  alerts are Included on every tier, which is why this node is only
 *  PARTIALLY gated (backend: PARTIALLY_GATED_NODE_TYPES). */
const PAID_CHANNELS = ['slack', 'teams']

const InternalAlertConfig: React.FC<InternalAlertConfigProps> = ({ data, updateField }) => {
  const config = data.internal_alert_config || { channel: 'email', message_template: '', include_contact_details: true }
  const updateConfig = (key: string, value: any) => {
    updateField('internal_alert_config', { ...config, [key]: value })
  }

  // Slack/Teams alerts need a webhook — either in this node or in Settings →
  // Integrations. Load the user's integration config so we can warn (and block
  // save, see WorkflowBuilderPage) when neither is present.
  const settingsConfig = useSettingsStore((s) => s.config)
  const fetchConfig = useSettingsStore((s) => s.fetchConfig)
  useEffect(() => {
    if (!settingsConfig) fetchConfig()
  }, [settingsConfig, fetchConfig])

  const channel = config.channel || 'email'

  // Slack/Teams alerts are a paid feature. The options stay in the list --
  // an existing workflow already set to Slack has to keep displaying what it
  // is set to -- but they are marked, and selecting one raises a banner. The
  // backend refuses the same thing at activation and launch
  // (plan_gating.NODE_TYPE_FEATURES), so this only saves the user the trip.
  const { isLocked } = usePlanFeatures()
  const teamAlertsLocked = isLocked('team_alerts')
  const channelOptions = teamAlertsLocked
    ? CHANNEL_OPTIONS.map((o) =>
        PAID_CHANNELS.includes(o.value) ? { ...o, description: 'Requires a plan upgrade' } : o,
      )
    : CHANNEL_OPTIONS
  const channelLocked = teamAlertsLocked && PAID_CHANNELS.includes(channel)

  const settingsWebhookSet =
    channel === 'slack' ? !!settingsConfig?.slack_webhook_url_set
    : channel === 'teams' ? !!settingsConfig?.teams_webhook_url_set
    : false
  const nodeWebhook =
    channel === 'slack' ? config.slack_webhook_url
    : channel === 'teams' ? config.teams_webhook_url
    : undefined
  const webhookMissing =
    (channel === 'slack' || channel === 'teams') &&
    !((nodeWebhook || '').trim()) && !settingsWebhookSet

  return (
    <div className={sectionClass}>
      <Field label="Label">
        {(f) => (
          <input
            {...f}
            type="text"
            value={data.label || ''}
            onChange={(e) => updateField('label', e.target.value)}
            placeholder="Team Alert"
            className={inputClass} style={inputStyle}
          />
        )}
      </Field>

      <Field label="Channel">
        {(f) => (
          <Select
            {...f}
            value={config.channel || 'email'}
            onChange={(value) => updateConfig('channel', value)}
            options={channelOptions}
            className="w-full"
          />
        )}
      </Field>

      {channelLocked && (
        <div className={dangerBannerClass}>
          <LockSimple className="w-4 h-4 mt-0.5 flex-shrink-0" weight="fill" />
          <p className="text-xs">
            {channel === 'slack' ? 'Slack' : 'Microsoft Teams'} alerts are not included in your
            plan, so this workflow can't be activated while this channel is selected. Switch to
            Email or In-App, or compare plans in Billing.
          </p>
        </div>
      )}

      {webhookMissing && (
        <div className={dangerBannerClass}>
          <WarningCircle className="w-4 h-4 mt-0.5 flex-shrink-0" weight="fill" />
          <p className="text-xs">
            No {channel === 'slack' ? 'Slack' : 'Microsoft Teams'} webhook is configured. Add one below,
            or connect {channel === 'slack' ? 'Slack' : 'Microsoft Teams'} in Settings → Integrations.
            The workflow can't be saved until this is set.
          </p>
        </div>
      )}

      {config.channel === 'email' && (
        <Field label="Recipient Email">
          {(f) => (
            <input
              {...f}
              type="text"
              value={config.recipient_email || ''}
              onChange={(e) => updateConfig('recipient_email', e.target.value)}
              placeholder="team@company.com"
              className={inputClass} style={inputStyle}
            />
          )}
        </Field>
      )}

      {config.channel === 'slack' && (
        <Field
          label="Slack Webhook URL"
          hint={
            settingsWebhookSet
              ? 'Leave empty to use your Settings → Integrations Slack webhook.'
              : 'No Slack webhook saved in Settings → Integrations, so this field is required.'
          }
        >
          {(f) => (
            <input
              {...f}
              type="text"
              value={config.slack_webhook_url || ''}
              onChange={(e) => updateConfig('slack_webhook_url', e.target.value)}
              placeholder="https://hooks.slack.com/services/..."
              className={inputClass} style={inputStyle}
            />
          )}
        </Field>
      )}

      {config.channel === 'teams' && (
        <Field
          label="Teams Webhook URL"
          hint={
            settingsWebhookSet
              ? 'Leave empty to use your Settings → Integrations Teams webhook.'
              : 'No Teams webhook saved in Settings → Integrations, so this field is required.'
          }
        >
          {(f) => (
            <input
              {...f}
              type="text"
              value={config.teams_webhook_url || ''}
              onChange={(e) => updateConfig('teams_webhook_url', e.target.value)}
              placeholder="https://outlook.office.com/webhook/..."
              className={inputClass} style={inputStyle}
            />
          )}
        </Field>
      )}

      <Field label="Message Template">
        {(f) => (
          <TemplateInput
            {...f}
            value={config.message_template || ''}
            onChange={(v) => updateConfig('message_template', v)}
            placeholder="{{contact.full_name}} from {{contact.company_name}} has... (type {{ for variables)"
            rows={3}
          />
        )}
      </Field>

      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id="include_contact_details"
          checked={config.include_contact_details ?? true}
          onChange={(e) => updateConfig('include_contact_details', e.target.checked)}
          className={checkboxInputClass}
        />
        <label htmlFor="include_contact_details" className="text-sm text-foreground">
          Include contact details
        </label>
      </div>

      <div className={infoBannerClass}>
        <p className="text-xs">
          Send a notification to your team when a contact reaches this step.
        </p>
      </div>
    </div>
  )
}

export default InternalAlertConfig
